/**
 * Corner-clear span for opening sketches.
 *
 * A wall edge's `[0, length]` extent is measured on the CENTERLINE, so its
 * endpoints sit at the center of any crossing wall — an opening clamped to
 * raw 0 cuts half the crossing wall's thickness out of the corner. The
 * mitered face corners the wall renderer already computes mark where this
 * wall's faces clear the junction; the deeper of the two per end is the
 * intrusion-free bound, so an opening pushed into a corner lands exactly
 * flush with the orthogonal wall's inner face.
 *
 * Kept dependency-light (type-only skeleton import + pure arc math) so the
 * span logic is unit-testable without the viewer module graph.
 */

import type * as THREE from 'three';
import type { PointoramaWallSketch } from '../PointoramaWallSketch';
import { closestPointOnEdge, edgeLength as arcEdgeLength } from './arcGeometry';

/**
 * Along-edge span an opening may occupy without intruding into the walls
 * joined at this edge's end nodes. Dangling (cap) ends stay at 0 / length.
 */
export function openingClearSpanOnEdge(skeleton: PointoramaWallSketch, edgeId: string): { start: number; end: number } {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return { start: 0, end: 0 };
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return { start: 0, end: 0 };
  const aXY = { x: a.position.x, y: a.position.y };
  const bXY = { x: b.position.x, y: b.position.y };
  const bulge = edge.bulge ?? 0;
  const len = arcEdgeLength(aXY, bXY, bulge);
  const corners = skeleton.getMiteredCornersForEdge(edgeId);
  if (len <= 0 || !corners) return { start: 0, end: len };
  const along = (p: THREE.Vector2): number => closestPointOnEdge({ x: p.x, y: p.y }, aXY, bXY, bulge).s;
  const start = Math.min(len, Math.max(0, along(corners.startLeft), along(corners.startRight)));
  const end = Math.max(start, Math.min(len, along(corners.endLeft), along(corners.endRight)));
  return { start, end };
}

/**
 * Clamp a proposed opening offset so `[offset, offset + length]` stays inside
 * the edge's clear span (see {@link openingClearSpanOnEdge}). An opening
 * longer than the clear span centers on it instead (still clamped to the
 * edge itself), degrading to the closest-possible fit.
 */
export function clampOpeningOffsetToClearSpan(skeleton: PointoramaWallSketch, edgeId: string, length: number, offset: number): number {
  const span = openingClearSpanOnEdge(skeleton, edgeId);
  const maxOffset = span.end - length;
  if (maxOffset < span.start) {
    const edge = skeleton.getEdge(edgeId);
    const a = edge ? skeleton.getNode(edge.startNodeId) : undefined;
    const b = edge ? skeleton.getNode(edge.endNodeId) : undefined;
    const len = edge && a && b ? arcEdgeLength({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge ?? 0) : 0;
    const centered = (span.start + span.end - length) / 2;
    return Math.max(0, Math.min(Math.max(0, len - length), centered));
  }
  return Math.max(span.start, Math.min(maxOffset, offset));
}
